import _startCase from 'lodash/startCase';
import { useCallback, useEffect, useState } from 'react';

import { get, isEmpty } from 'ravenjs/utils/lodash';

import { APP_PERMISSIONS } from 'constants/permissions';
import { PREPPED_ANSWERS } from 'constants/tickets';
import Acl from 'modules/acl';
import { isPreppedAnswerExpired } from 'utils/casecave';

const defaultParams = {
    include: [
        'content_summary_links',
        'vpa_requests',
        'prepped_answer_transitions',
        'vpa_requests.vpa_request_transitions',
        'suggested_edits',
        'suggested_edits.prepped_answer_suggested_edit_transitions',
    ],
};

export function usePreppedAnswersDetails({
    completeVpa,
    currentUserId,
    declineVpa,
    deletePreppedAnswer,
    downgradeVpa,
    favoritePreppedAnswer,
    fetchComments,
    fetchPreppedAnswerDetails,
    fetchCaseResourceTypes,
    fetchUser,
    id,
    nominatePreppedAnswer,
    unFavoritePreppedAnswer,
}) {
    const [data, setData] = useState({});
    const [permissions, setPermissions] = useState({});
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const getTopics = useCallback(async attributes => {
        const topics = [];
        const topicsSummary = get(attributes, 'topics_summary', {
            topics: [],
            sub_topics: [],
            micro_topics: [],
        });

        const parentTopics = get(topicsSummary, 'topics', []);
        const subTopics = get(topicsSummary, 'sub_topics', []);
        const microTopics = get(topicsSummary, 'micro_topics', []);

        for (const parentTopic of parentTopics) {
            const subTopic = subTopics.find(subTopic => subTopic.parent_id === parentTopic.id);

            if (subTopic === undefined) {
                topics.push({
                    parentTopic,
                    subTopic: null,
                    microTopic: null,
                });
            } else {
                const microTopic = microTopics.find(
                    microTopic => microTopic.parent_id === subTopic.id
                );

                if (microTopic === undefined) {
                    topics.push({
                        parentTopic,
                        subTopic,
                        microTopic: null,
                    });
                } else {
                    topics.push({
                        parentTopic,
                        subTopic,
                        microTopic,
                    });
                }
            }
        }

        return topics;
    }, []);

    function getSummary(preppedAnswer) {
        const { key_phrases, regions, kind, shared } = preppedAnswer;
        const allRegions = !isEmpty(regions)
            ? regions
                  .map(region => region)
                  .sort()
                  .join(', ')
            : '-';

        const summary = {
            answerUsedGenerativeAI: preppedAnswer.answer_used_generative_ai
                ? PREPPED_ANSWERS.ANSWER_USED_GENERATIVE_AI.TRUE
                : PREPPED_ANSWERS.ANSWER_USED_GENERATIVE_AI.FALSE,
            isPublic: shared,
            keyPhrases: isEmpty(key_phrases) ? '-' : key_phrases,
            states: allRegions,
            kind: isEmpty(kind) ? '-' : _startCase(kind),
        };

        return summary;
    }

    function getContentLinks(contents) {
        return contents;
    }

    function getNominatedCurrentState(currentState) {
        switch (currentState) {
            case PREPPED_ANSWERS.DETAILS.MENU_ITEMS.NOMINATED_KEY: {
                return {
                    label: PREPPED_ANSWERS.DETAILS.MENU_ITEMS.NOMINATED_FOR_VPA,
                    disable: true,
                };
            }
            case PREPPED_ANSWERS.DETAILS.MENU_ITEMS.APPROVED_NOMINATED_KEY: {
                return {
                    label: PREPPED_ANSWERS.DETAILS.MENU_ITEMS.SUGGEST_UPDATE,
                    disable: !Acl.check(APP_PERMISSIONS.systemCaseCaveVPASuggestUpdate),
                };
            }
            default: {
                return {
                    label: PREPPED_ANSWERS.DETAILS.MENU_ITEMS.NOMINATE_FOR_VPA,
                    disable: !Acl.check(APP_PERMISSIONS.systemCaseCavePANominate),
                };
            }
        }
    }

    async function onClickFavorite() {
        try {
            setLoading(true);

            if (data?.isFavorite) {
                await unFavoritePreppedAnswer({ id });
                setData({
                    ...data,
                    isFavorite: false,
                });
            } else {
                await favoritePreppedAnswer({ id });
                setData({
                    ...data,
                    isFavorite: true,
                });
            }
        } catch (error) {
            setError(error);
        } finally {
            setLoading(false);
        }
    }

    async function onClickNominate(payload) {
        let success = false;
        try {
            setLoading(true);
            await nominatePreppedAnswer(payload);
            success = true;
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }

        return success;
    }

    async function onClickComplete(id) {
        let success = false;
        try {
            setLoading(true);
            await completeVpa({ id });
            success = true;
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }

        return success;
    }

    async function onClickDecline(id) {
        let success = false;
        try {
            setLoading(true);
            await declineVpa({ id });
            success = true;
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }

        return success;
    }

    async function onClickDowngrade(id) {
        let success = false;
        try {
            setLoading(true);
            await downgradeVpa({ id });
            success = true;
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }

        return success;
    }

    async function onClickDelete(id) {
        let success = false;
        try {
            setLoading(true);
            await deletePreppedAnswer({ id });
            success = true;
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }

        return success;
    }

    function checkEditPermission(preppedAnswer, userId) {
        const { current_state, user_id: creatorId, default: defaultSignature } = preppedAnswer;

        if (defaultSignature && !Acl.check(APP_PERMISSIONS.systemCaseCavePAEditDefault)) {
            return false;
        }

        if (current_state === PREPPED_ANSWERS.DETAILS.MENU_ITEMS.APPROVED_NOMINATED_KEY) {
            if (Acl.check(APP_PERMISSIONS.systemCaseCaveVPAEditOthers)) {
                return true;
            } else if (creatorId === userId) {
                return false;
            }
        }

        if (
            (creatorId === userId && Acl.check(APP_PERMISSIONS.systemCaseCavePAEditAndDeleteOwn)) ||
            Acl.check(APP_PERMISSIONS.systemCaseCavePAEditAndDeleteOthers)
        ) {
            return true;
        }

        return false;
    }

    function checkDeletePermission(preppedAnswer, userId) {
        const { current_state, user_id: creatorId, default: defaultSignature } = preppedAnswer;

        if (defaultSignature) {
            return false;
        }

        if (current_state === PREPPED_ANSWERS.DETAILS.MENU_ITEMS.APPROVED_NOMINATED_KEY) {
            if (
                (creatorId === userId && Acl.check(APP_PERMISSIONS.systemCaseCaveVPADeleteOwn)) ||
                Acl.check(APP_PERMISSIONS.systemCaseCaveVPADeleteOthers)
            ) {
                return true;
            }
        }
        if (
            (creatorId === userId && Acl.check(APP_PERMISSIONS.systemCaseCavePAEditAndDeleteOwn)) ||
            Acl.check(APP_PERMISSIONS.systemCaseCavePAEditAndDeleteOthers)
        ) {
            return true;
        }

        return false;
    }

    async function refetchData() {
        await fetchInitialData();
    }

    const getCommentsCount = useCallback(
        async id => {
            const commentParams = {
                'filter[prepped_answer_id]': id,
                paginate: false,
            };
            const commentsResponse = await fetchComments({ ...commentParams });
            const commentCount = get(commentsResponse, 'data', []).length;
            return commentCount;
        },
        [fetchComments]
    );

    const getPreppedAnswersDetailsData = useCallback(
        async ({ params, id }) => {
            try {
                const response = await fetchPreppedAnswerDetails({
                    params,
                    id,
                });

                const preppedAnswerId = get(response, 'data.id', '');
                const preppedAnswerAttributes = get(response, 'data.attributes', {});
                const preppedAnswerIncludedData = get(response, 'included', {});

                const userId = get(preppedAnswerAttributes, 'user_id', '');
                const isDeletedPreppedAnswer = !isEmpty(
                    get(preppedAnswerAttributes, 'deleted_at', null)
                );

                const expirationDate = get(preppedAnswerAttributes, 'expiration_date', null);
                const isExpiredPreppedAnswer = isPreppedAnswerExpired(expirationDate);

                const resourceTypesResponse = await fetchCaseResourceTypes();
                const resourceTypes = resourceTypesResponse.reduce(
                    (selectableResourceTypes, option) => {
                        if (get(option, 'attributes.user_selectable', false)) {
                            selectableResourceTypes.push({
                                value: option.id,
                                label: option.attributes.label,
                                description: option.attributes.description,
                            });
                        }
                        return selectableResourceTypes;
                    },
                    []
                );

                return {
                    isExpiredPreppedAnswer,
                    isDeletedPreppedAnswer,
                    preppedAnswerAttributes,
                    preppedAnswerId,
                    preppedAnswerIncludedData,
                    resourceTypes,
                    userId,
                };
            } catch (e) {
                setError({
                    message: e.message,
                    status: e.response.status,
                });
            }
            return null;
        },
        [fetchPreppedAnswerDetails, fetchCaseResourceTypes]
    );

    function isFavorite(attributes, currentUserId) {
        const favoriteIds = get(attributes, 'favorited_by_ids', []);
        const isFavorite = Boolean(favoriteIds.find(id => id === currentUserId));

        return isFavorite;
    }

    function getIncludedData(includedData) {
        const contentAttributes = [];
        const vpaRequestAttributes = [];
        const suggestedUpdatesAttributes = [];
        const suggestUpdateTransitionsAttributes = [];
        const vpaRequestTransitionsAttributes = [];
        const preppedAnswerTransitionsAttributes = [];
        const preppedAnswerModificationsAttributes = [];

        if (!isEmpty(includedData)) {
            includedData.forEach(data => {
                const { attributes, id, type } = data;
                if (type === 'content_summary_links') contentAttributes.push(attributes);
                else if (type === 'vpa_requests')
                    vpaRequestAttributes.push({ vpaRequestId: id, ...attributes });
                else if (type === 'suggested_edits') {
                    suggestedUpdatesAttributes.push({ suggestedUpdateId: id, ...attributes });
                } else if (type === 'suggested_edit_transitions') {
                    suggestUpdateTransitionsAttributes.push({
                        suggestedUpdateTransitionId: id,
                        ...attributes,
                    });
                } else if (type === 'vpa_request_transitions')
                    vpaRequestTransitionsAttributes.push({
                        vpaRequestId: id,
                        ...attributes,
                    });
                else if (type === 'prepped_answer_transitions')
                    preppedAnswerTransitionsAttributes.push({
                        vpaRequestId: id,
                        ...attributes,
                    });
                else if (type === 'prepped_answer_modifications')
                    preppedAnswerModificationsAttributes.push({
                        preppedAnswerModificationId: id,
                        ...attributes,
                    });
            });
        }

        const vpaRequests = vpaRequestAttributes.filter(({ status }) => status === 'active');
        const vpaRequestId = vpaRequests.length ? get(vpaRequests[0], 'vpaRequestId') : null;

        const suggestedUpdates = suggestedUpdatesAttributes.filter(
            ({ current_state }) => current_state === 'open'
        );
        const suggestedUpdateId = suggestedUpdates.length
            ? get(suggestedUpdates[0], 'suggestedUpdateId')
            : null;

        return {
            contentAttributes,
            preppedAnswerTransitionsAttributes,
            preppedAnswerModificationsAttributes,
            vpaRequestAttributes,
            vpaRequestId,
            vpaRequests,
            suggestedUpdateId,
            suggestedUpdates,
            suggestedUpdatesAttributes,
            suggestUpdateTransitionsAttributes,
            vpaRequestTransitionsAttributes,
        };
    }

    const getHistoryCount = () => {
        const {
            suggestedUpdatesAttributes,
            vpaRequestAttributes,
            vpaRequestTransitionsAttributes,
            preppedAnswerTransitionsAttributes,
            preppedAnswerModificationsAttributes,
        } = data;

        return (
            (preppedAnswerTransitionsAttributes || []).filter(
                item => item.event_name === 'nominated' || item.event_name === 'downgraded'
            ).length +
            (suggestedUpdatesAttributes || []).filter(
                item => item.current_state === 'approved' || item.current_state === 'declined'
            ).length +
            (vpaRequestAttributes || []).filter(
                item =>
                    (item.kind === 'nomination' &&
                        (item.status === 'completed' || item.status === 'cancelled')) ||
                    item.kind === 'update'
            ).length +
            (vpaRequestTransitionsAttributes || []).filter(item => {
                if (item.kind === 'update') {
                    const filteredData = vpaRequestTransitionsAttributes.find(
                        item => item.vpa_request_id === vpaRequestAttributes.vpaRequestId
                    );
                    return (
                        filteredData?.to_state === 'completed' ||
                        filteredData?.to_state === 'cancelled'
                    );
                }
                return false;
            }).length +
            (preppedAnswerModificationsAttributes || []).filter(item => {
                const { event, object_changes } = item;
                return (
                    event === 'update' &&
                    (object_changes?.expiration_date?.length > 0 ||
                        object_changes?.user_id?.length > 0)
                );
            }).length
        );
    };

    function getVPAOwner(preppedAnswerModificationsAttributes) {
        if (!Array.isArray(preppedAnswerModificationsAttributes)) {
            return '';
        }

        for (const {
            event,
            object_changes,
            username,
            user_id,
        } of preppedAnswerModificationsAttributes) {
            if (
                event === 'update' &&
                object_changes?.current_state?.length > 0 &&
                object_changes?.current_state[0] ===
                    PREPPED_ANSWERS.DETAILS.MENU_ITEMS.NOMINATED_KEY &&
                object_changes?.current_state[1] ===
                    PREPPED_ANSWERS.DETAILS.MENU_ITEMS.APPROVED_NOMINATED_KEY
            ) {
                return { name: username, id: user_id };
            }
        }

        return '';
    }

    const fetchInitialData = useCallback(
        async (loading = true) => {
            try {
                setLoading(loading);
                const additionalDefaultParams = {
                    ...defaultParams,
                };

                additionalDefaultParams.include.push('prepped_answer_modifications');
                additionalDefaultParams.include.push('resources');

                const {
                    isDeletedPreppedAnswer,
                    preppedAnswerAttributes,
                    preppedAnswerId,
                    preppedAnswerIncludedData,
                    userId,
                    isExpiredPreppedAnswer,
                    resourceTypes,
                } = await getPreppedAnswersDetailsData({
                    params: additionalDefaultParams,
                    id,
                });

                const {
                    contentAttributes,
                    preppedAnswerTransitionsAttributes,
                    preppedAnswerModificationsAttributes,
                    suggestUpdateTransitionsAttributes,
                    suggestedUpdateId,
                    suggestedUpdates,
                    suggestedUpdatesAttributes,
                    vpaRequestAttributes,
                    vpaRequestId,
                    vpaRequests,
                    vpaRequestTransitionsAttributes,
                } = getIncludedData(preppedAnswerIncludedData);

                const resources = [];
                if (!isEmpty(preppedAnswerIncludedData)) {
                    preppedAnswerIncludedData.forEach(data => {
                        const { type } = data;
                        if (type === 'external_resources') resources.push(data);
                    });
                }

                const commentCount = await getCommentsCount(id);
                const resourceCount = get(preppedAnswerAttributes, 'resource_ids', []).length;

                if (!isEmpty(userId)) {
                    const userResponse = await fetchUser(userId);

                    const preppedAnswer = {
                        id: preppedAnswerId,
                        author: userResponse.fullName,
                        ...preppedAnswerAttributes,
                    };

                    setData({
                        answer: preppedAnswer.body,
                        author: preppedAnswer.author,
                        bodyUpdatedAt: preppedAnswer.body_updated_at,
                        commentCount,
                        contentAttributes,
                        contentLinks: getContentLinks(contentAttributes),
                        createdAt: preppedAnswer.created_at,
                        expirationDate: preppedAnswer.expiration_date,
                        isDeletedPreppedAnswer,
                        isExpiredPreppedAnswer,
                        isFavorite: isFavorite(preppedAnswerAttributes, currentUserId),
                        isVPA:
                            preppedAnswer.current_state ===
                            PREPPED_ANSWERS.DETAILS.MENU_ITEMS.APPROVED_NOMINATED_KEY,
                        isNominated:
                            preppedAnswer.current_state ===
                            PREPPED_ANSWERS.DETAILS.MENU_ITEMS.NOMINATED_KEY,
                        isNominationPending:
                            preppedAnswer.current_state ===
                            PREPPED_ANSWERS.DETAILS.MENU_ITEMS.NOMINATE_KEY,
                        nominateButtonLabel: getNominatedCurrentState(preppedAnswer.current_state)
                            .label,
                        preppedAnswer,
                        preppedAnswerModificationsAttributes,
                        preppedAnswerTransitionsAttributes,
                        suggestedUpdateId,
                        suggestedUpdates,
                        suggestedUpdatesAttributes,
                        suggestUpdateTransitionsAttributes,
                        resourceCount,
                        resources,
                        resourceTypes,
                        summary: getSummary(preppedAnswer),
                        title: preppedAnswer.title,
                        topics: await getTopics(preppedAnswerAttributes),
                        updatedAt: preppedAnswer.updated_at,
                        vettedPAOwner: getVPAOwner(preppedAnswerModificationsAttributes),
                        vpaRequestAttributes,
                        vpaRequestId,
                        vpaRequests,
                        vpaRequestTransitionsAttributes,
                    });

                    setPermissions({
                        clone: Acl.check(APP_PERMISSIONS.systemCaseCavePACreate),
                        delete: checkDeletePermission(preppedAnswer, currentUserId),
                        edit: checkEditPermission(preppedAnswer, currentUserId),
                        history: Acl.check(APP_PERMISSIONS.systemCaseCavePAView),
                        viewComment: Acl.check(APP_PERMISSIONS.systemCaseCavePAView),
                        viewResource: Acl.check(APP_PERMISSIONS.systemCaseCavePAView),
                    });
                }
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        },
        [currentUserId, fetchUser, getCommentsCount, getPreppedAnswersDetailsData, getTopics, id]
    );

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    return {
        data,
        error,
        historyCount: getHistoryCount(),
        isShowUpdateRequest: !isEmpty(data.vpaRequests) || !isEmpty(data.suggestedUpdates),
        loading,
        onClickComplete,
        onClickDecline,
        onClickDelete,
        onClickDowngrade,
        onClickFavorite,
        onClickNominate,
        permissions,
        refetchData,
    };
}
